<script setup>
import { reactive, computed } from 'vue';
import { storeToRefs } from 'pinia';
import ProfilePicture from '@components/ProfilePicture.vue';
import Spinner from '@components/Spinner.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { flashBag, flashBagError } from '@tools/flashbag';
import { trans } from '@elements/translation';
import Logger from '@tools/logger/logger';
import { FlashBagType } from '@enums/flashbag';
import useOrganizationStore from '../../store/organization';
import DeleteTeamMemberButton from './Button/DeleteTeamMemberButton.vue';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isInvited: {
    type: Boolean,
    default: false,
  },
});

const store = useOrganizationStore();
const { currentUserId } = store;

const { isMobile } = storeToRefs(useScreensizeStore());

const { user, index } = props;
const dateFormat = new Intl.DateTimeFormat(window.locale);
const timeFormat = new Intl.DateTimeFormat(window.locale, {
  hour: 'numeric',
  minute: 'numeric',
});

const isConnectedContact = computed(() => currentUserId === user.id);

const loader = reactive({
  deleting: false,
});

function doDelete() {
  loader.deleting = true;
  store
    .deleteUser(user.id, props.isInvited)
    .then(() => {
      let flashBagType = FlashBagType.INFO;

      if (props.isInvited) {
        const { invitations } = store;
        invitations.splice(index, 1);

        store.$patch({
          invitations,
        });

        flashBagType = FlashBagType.SUCCESS;
      } else {
        const { team } = store;
        team.splice(index, 1);

        store.$patch({
          team,
        });
      }

      flashBag(flashBagType, trans('user.organization.team.deleted'));
    })
    .catch((e) => {
      flashBagError();
      Logger.error('Error catched', e);
    })
    .finally(() => {
      loader.deleting = false;
    });
}
</script>

<template>
  <div class="team-row team-list__row table__row">
    <div class="team-row-name__wrapper">
      <div class="team-row__name team-row-name">
        <profile-picture
          v-if="!isInvited"
          :uuid="user.profile_picture?.uuid"
          :user-firstname="user.firstname"
          class="team-row-name__picture"
        />
        <div v-if="!user.enabled && !isMobile" class="team-row-name__picture-space" />
        <div class="team-row-name__infos team-truncate">
          <div v-show="user.enabled" class="team-row-name-display">
            <div class="team-truncate">
              {{ user.firstname }} <b>{{ user.lastname }}</b>
            </div>
            <div v-if="isConnectedContact" class="team-row-name__you">
              ({{ trans('contacts.body.table.you') }})
            </div>
          </div>
          <div class="team-row-name__email team-truncate" :class="{ 'alone-mail': !user.enabled }">
            {{ user.email }}
          </div>
        </div>
      </div>
    </div>

    <div class="team-row-name__infos">
      <span v-if="user.created_at">
        <span v-show="isMobile" class="team-column-name">
          {{ trans('user.organization.team.created_at') }} :
        </span>
        {{ dateFormat.format(new Date(user.created_at.iso)) }}
      </span>
    </div>

    <div>
      <div v-if="user.last_login && user.enabled" class="team-row-name__infos-date">
        <div v-show="isMobile" class="team-column-name">
          {{ trans('user.organization.team.last_login') }} :&nbsp;
        </div>
        <div>
          {{ dateFormat.format(new Date(user.last_login.iso)) }} -
          {{ timeFormat.format(new Date(user.last_login.iso)) }}
        </div>
      </div>
      <div v-else-if="isInvited" class="team-row-name__infos-date">
        <div v-show="isMobile" class="team-column-name">
          {{ trans('user.organization.team.last_login') }} :&nbsp;
        </div>
        <div class="team-type team-type__primary">
          {{ trans('user.organization.team.invitation_sent') }}
        </div>
      </div>
    </div>

    <div class="team-row__actions row-actions">
      <div class="row-actions-grouped">
        <delete-team-member-button v-show="!loader.deleting && !user.deleting_at" :disabled="isConnectedContact" @delete="doDelete" />
        <spinner v-show="loader.deleting || user.deleting_at" />
      </div>
    </div>
  </div>
</template>
